import React, { useRef } from 'react'
import { useDraggable } from 'react-use-draggable-scroll'
import { styled } from '@material-ui/core'
import { MouseDrag } from 'components/icons'
import { cadastrarCreditos } from 'store/modules/api/creditos-biodiversidade/creditos-biodiversidade.api'
import { FieldCell, FooterCell, Project, UnitCard } from './components'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { enqueueSnackbar } from 'store/modules/notifications'

const Columns = styled('div')(({ theme }) => ({
  display: 'grid',
  gridAutoFlow: 'column',
  columnGap: 20,
  position: 'relative',
  width: '100%',
  overflowX: 'auto',
  paddingBottom: 40,
  gridAutoColumns: 'min-content',
}))

const Column = styled('div')(({ theme, totalRows }) => ({
  minWidth: '270px',
  display: 'grid',
  gridTemplateRows: 'subgrid',
  gridRow: `1 / ${totalRows}`,
}))

Column.displayName = 'Column'

const SidePanel = styled('aside')(({ theme, totalRows }) => ({
  borderRight: '1px solid #DFDFDF',
  padding: theme.spacing(0, 1),
  background: theme.palette.common.white,
  display: 'grid',
  gridTemplateRows: 'subgrid',
  minWidth: '400px',
  maxWidth: '400px',
  gridRow: `1 / ${totalRows}`,
  position: 'sticky',
  left: 0,
  top: 0,

  zIndex: 10,
}))

const SidePanelTitle = styled('h1')(({ theme }) => ({
  fontSize: 20,
  fontWeight: 400,
  color: '#454545',
}))

const DragMessage = styled('div')(({ theme }) => ({
  position: 'absolute',
  left: 'calc(100% + 28px)',
  top: 25,
  bgcolor: 'red',
  display: 'flex',
  alignItems: 'center',
  width: 'auto',
  whiteSpace: 'nowrap',
  color: '#8C8C8C',
  fontSize: 12,
  gap: 5,
}))

export const ProjectsBoard = ({ credits = {}, refetch }) => {
  const ref = useRef()
  const { events } = useDraggable(ref)

  const { t } = useTranslation()
  const dispatch = useDispatch()

  const totalRows = (credits?.projetos?.length ?? 0) + 4

  const handleFieldChange = async ({ projetoId, credito, unidadeId }) => {
    try {
      await cadastrarCreditos({ projetoId, credito: Number(credito), unidadeId })
      refetch()
      dispatch(
        enqueueSnackbar({
          message: t('creditosDeBiodiversidade.pontuacaoAtualizada'),
          options: { variant: 'success', autoHideDuration: 3000, dismissible: true },
        }),
      )
      return true
    } catch (error) {
      dispatch(
        enqueueSnackbar({
          message: t(`creditosDeBiodiversidade.${error.message}`),
          options: { variant: 'warning', autoHideDuration: 3000, dismissible: true },
        }),
      )
      return false
    }
  }

  const getCellCreditosValue = (projectId, unitId) => {
    const cellCredit = credits.creditos.find(
      ({ projetoId, unidadeId }) => projetoId === projectId && unidadeId === unitId,
    )

    if (!cellCredit) return '0'

    const value = cellCredit.credito
      .toLocaleString('en-US', {
        style: 'decimal',
        currency: 'USD',
        minimumFractionDigits: 2,
      })
      .replaceAll(',', '')

    return value
  }

  return (
    <Columns {...events} ref={ref}>
      <SidePanel totalRows={totalRows}>
        <DragMessage>
          <MouseDrag />
          {t('creditosDeBiodiversidade.arrasteParaVerOsProjetos')}
        </DragMessage>

        <SidePanelTitle>{t('creditosDeBiodiversidade.projetosConservacao')}</SidePanelTitle>

        <div />

        {credits.projetos?.map(project => {
          return <Project key={`project-${project.id}`} project={project} />
        })}

        <div />
      </SidePanel>

      {credits.unidades?.map((unit, index) => {
        return (
          <Column key={`unit-${unit.id}}`} totalRows={totalRows}>
            <div />
            <UnitCard index={index} unit={unit} />

            {credits.projetos?.map(project => (
              <FieldCell
                key={`${project.id}-${unit.id}}`}
                value={getCellCreditosValue(project.id, unit.id)}
                disabled={unit.ecorregiao === 'Não possui'}
                onChange={value =>
                  handleFieldChange({
                    credito: value,
                    projetoId: project.id,
                    unidadeId: unit.id,
                  })
                }
              />
            ))}

            <FooterCell value={unit.saldoTotalDbp} />
          </Column>
        )
      })}
    </Columns>
  )
}
